// Versioned binary formats for authoring evidence (achievement authoring spec
// 6.5, 7.13, 8.4, 8.5, 8.6). Pure: gzip and gunzip are injected by the caller
// ({ gzip, gunzip, sha256Hex }), so the same code runs in the studio worker,
// on the server and in the database-free Functions.
//
// Every file: a 12-byte preamble (little-endian), the header as UTF-8 JSON,
// then the body.
//   0..3   magic, ASCII: RPAM memory, RPAF frames, RPAC checkpoint
//   4..5   u16 format version (1)
//   6..7   u16 reserved (0)
//   8..11  u32 header length in bytes (2 to 262,144)
// Headers are stored uncompressed, so the server validates them without
// inflating anything; the declared bodyBytes is checked against the decoded
// cap, and whoever inflates a body passes exactly bodyBytes as the limit.
//
// memory body (stored gzip, or raw when CompressionStream is unavailable):
//   one record per header sample, in order
//   keyframe: u8 1, then sampleBytes bytes (every recordable window, in order)
//   diff:     u8 2, u32 run count, then per run u32 offset, u32 length, bytes
// frames body (never gzip; JPEG is already compressed):
//   per frame: u32 length, then the JPEG bytes
// checkpoint body (stored gzip, or raw):
//   u8 3, the save object as UTF-8 JSON (saveBytes), then the recordable-window
//   memory (memoryBytes: 0 or sampleBytes)
//
// Retained bytes (spec 6.5, 8.8): an encoder checks every count and size cap
// before it reads, maps or allocates anything sized by them, computes each
// buffer's exact size before allocating it, writes every file straight into
// one buffer of its final size, and gives the injected gzip an output limit
// (the room the stored cap leaves for the body) that gzip stops at.
// deps.reserve(bytes, what), when given, is called before every allocation an
// encoder makes ('header', 'state JSON', 'body', 'compressed body' at its
// limit, 'file'); it may throw to refuse, and the encoder then allocates
// nothing more. The encoder never releases a charge: their sum bounds the
// call's retained-byte peak, and the caller releases it when the call settles.
//
// Decoders: the caller charges the inflated body (sampleBytes + bodyBytes for a
// memory recording, bodyBytes for a checkpoint, both from the header) before it
// inflates. Every decoder also takes an optional deps.reserve(bytes, what),
// called before each allocation the caller cannot size from outside:
//   'header text', 'parsed header'  every decoder, before the header is decoded
//                  and parsed (the header's shape is scanned first, see below);
//   'record index' decodeMemoryRecording, before the per-sample offsets;
//   'frame index'  decodeFramesHeader and decodeFrames, before the per-frame views;
//   'save text', 'save object', 'state bytes'  decodeCheckpointEnvelope.
// It throws to refuse, the decoder then allocates nothing more, and the
// caller's own error reaches the caller unchanged, as an encoder's does.
//
// JSON text is never parsed before its shape is known: a linear scan of its
// UTF-8 bytes counts its objects, arrays, members and values and its deepest
// nesting, refuses a text past its kind's depth or container cap, and the
// parsed graph is charged from those counts (JSON_HEAP_COSTS) before the text is
// decoded. A header is at most 3 deep with at most 1,032 objects and arrays; a
// libretro save is one flat object; a jsnes save nests its state at most 64
// deep (the encoder's rule) with at most 65,536 objects and arrays.
import { AUTHORING_SYSTEMS, FILE_CAPS, FORMAT_RE, FRAME_SOURCES, TIMINGS } from './authoring-limits.js';
import { recordableSnapshotBytes, recordableWindowsFor } from './authoring-windows.js';
import { isGzip } from './save-state-codec.js';

export const EVIDENCE_FORMAT_VERSION = 1;
export const EVIDENCE_MAGIC = Object.freeze({ memory: 'RPAM', frames: 'RPAF', checkpoint: 'RPAC' });
export const EVIDENCE_PREAMBLE_BYTES = 12;
export const EVIDENCE_HEADER_BYTES_MAX = 262_144;
export const EVIDENCE_ERROR_CODES = Object.freeze(['bad_magic', 'bad_version', 'too_large', 'truncated', 'bad_header']);

const RECORD_KEYFRAME = 1;
const RECORD_DIFF = 2;
const RECORD_CHECKPOINT = 3;
const RUN_OVERHEAD = 8; // u32 offset + u32 length
const DIFF_OVERHEAD = 5; // u8 tag + u32 run count
const INT32_MAX = 2_147_483_647;
const MAX_SAFE = Number.MAX_SAFE_INTEGER;
const SAVE_MODE_RE = /^[a-z_]{1,32}$/;
const MEMORY_HEADER_KEYS = [
  'format', 'system', 'romHash', 'coreName', 'coreIdentity', 'authorSessionId', 'segmentId', 'frameSource',
  'windows', 'capabilities', 'preEventMs', 'bodyBytes', 'samples',
];
const FRAMES_HEADER_KEYS = {
  frames: ['format', 'kind', 'system', 'romHash', 'coreName', 'coreIdentity', 'authorSessionId', 'segmentId', 'frames'],
  test_frames: ['format', 'kind', 'system', 'romHash', 'coreName', 'coreIdentity', 'testSessionId', 'frames'],
};
const FRAME_ENTRY_KEYS = { frames: ['sampleIndex', 't', 'width', 'height'], test_frames: ['t', 'width', 'height'] };
const FRAMES_STORED_MAX = Math.max(FILE_CAPS.frames.storedBytes, FILE_CAPS.test_frames.storedBytes);
const CHECKPOINT_HEADER_KEYS = [
  'format', 'system', 'romHash', 'coreName', 'coreIdentity', 'authorSessionId', 'segmentId', 't', 'memoryT',
  'windows', 'saveBytes', 'memoryBytes', 'bodyBytes',
];
// checkpoint_near and checkpoint_early share their caps (spec 6.5).
const CHECKPOINT_CAPS = FILE_CAPS.checkpoint_near;
const SAVE_KEYS = ['system', 'romHash', 'coreName', 'mode', 'isMultiDisk', 'frame', 'emulatorState'];
// Spec 8.6 names the C64 multi-disk bundle's base64 romDataB64 (main.js holds it
// as app._romDataB64); a manual save carries the same text as romData, the name
// the restore path reads (checkpointRestoreSave maps one to the other).
const BUNDLE_KEY = 'romDataB64';
// jsnes states are plain JSON objects; every libretro core's state is bytes
// (base64 in the stored save), as encodeEmulatorState writes it (spec 8.6).
const JSNES_CORE = 'jsnes';
// State bytes per btoa call: a multiple of 3, so the chunks' base64 joins into
// exactly the text u8ToBase64 (and so encodeEmulatorState) produces.
const BASE64_CHUNK = 0x6000;
// Base64 characters per atob call when decoding, a multiple of 4 so only the
// last chunk can carry padding: the decoder's one fixed scratch string.
const BASE64_DECODE_CHUNK = 0x8000;
// The heap a parsed JSON graph can cost, charged from the shape of its text
// (scanJsonShape) before the text is decoded or parsed. Each construct's
// allowance is above what it was measured to cost on Node 24 (64-bit, no
// pointer compression, so at least what a browser engine spends): a string
// character at most 2 bytes; a value an 8-byte slot plus a 16-byte heap number
// or string header; an object member a key string plus a dictionary-mode entry
// (about 74 bytes, unique keys); an object or array 40 to 64 bytes with its
// backing store (an array of empty objects, the densest container text, is 21x
// its text, all of it charged here). A real jsnes state (about 1.5 MB of text,
// 1,669 objects and arrays, 5 deep) parses to about 3 MB and is charged about
// 15 MB.
export const JSON_HEAP_COSTS = Object.freeze({ perTextByte: 2, perValue: 32, perMember: 96, perContainer: 128 });
// The encoder's nesting rule for a jsnes state (its own objects and arrays,
// emulatorState itself included), applied again when a save is decoded.
const JSON_DEPTH_MAX = 64;
// Objects and arrays in one jsnes state: a real one has about 1,700.
const JSNES_JSON_CONTAINERS_MAX = 65_536;
// A header nests at most 3 deep (header, samples or frames or windows, entry)
// and holds at most one object per sample or frame plus 8 more.
const HEADER_JSON_DEPTH_MAX = 3;
const HEADER_JSON_CONTAINERS_MAX = Math.max(
  FILE_CAPS.memory.samplesMax, FILE_CAPS.frames.framesMax, FILE_CAPS.test_frames.framesMax,
) + 8;
/** The decoders' JSON shape limits (see the top of this file). */
export const EVIDENCE_JSON_LIMITS = Object.freeze({
  headerDepthMax: HEADER_JSON_DEPTH_MAX,
  headerContainersMax: HEADER_JSON_CONTAINERS_MAX,
  jsnesStateDepthMax: JSON_DEPTH_MAX,
  jsnesStateContainersMax: JSNES_JSON_CONTAINERS_MAX,
});
// A decoded memory recording's per-sample offsets: 4 bytes each, plus the
// typed array and its buffer (about 240 bytes measured).
const RECORD_INDEX_BASE_BYTES = 512;
// A decoded frame sequence's per-frame JPEG view (about 113 bytes measured,
// its array slot included) and the dimensions read from it (about 49 bytes).
const FRAME_INDEX_BYTES_PER_FRAME = 256;
// The header's JSON text and its UTF-8 bytes, charged at the header cap before
// the header is built (the sample and frame counts bound it below the cap).
const HEADER_RESERVE_BYTES = 2 * EVIDENCE_HEADER_BYTES_MAX;

/** `options.cause` keeps the failure that was converted (a JSON, UTF-8, gunzip, gzip or state decoder error). */
export class EvidenceFormatError extends Error {
  constructor(code, detail, options) {
    if (!EVIDENCE_ERROR_CODES.includes(code)) throw new TypeError(`unknown evidence error code ${String(code)}`);
    super(detail ? `${code}: ${detail}` : code, options);
    this.name = 'EvidenceFormatError';
    this.code = code;
  }
}

function fail(code, detail, cause) {
  throw new EvidenceFormatError(code, detail, cause === undefined ? undefined : { cause });
}

// Charge one allocation to the caller's byte account before making it.
function reserve(deps, bytes, what) {
  if (typeof deps?.reserve === 'function') deps.reserve(bytes, what);
}

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
const isUint = (value, max) => Number.isSafeInteger(value) && value >= 0 && value <= max;
const isTime = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const textEncoder = new TextEncoder();

function isPlainObject(value) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function exactKeys(obj, required, optional, what) {
  for (const key of Object.keys(obj)) {
    if (!required.includes(key) && !optional.includes(key)) fail('bad_header', `${what}: unknown key ${key}`);
  }
  for (const key of required) if (!has(obj, key)) fail('bad_header', `${what}: missing ${key}`);
}

// The shape of a JSON text, read from its UTF-8 bytes without decoding them
// (every byte of a multi-byte character is 0x80 or above, so none is taken for
// a quote, a backslash or a bracket): its objects and arrays, its members (one
// per key) and an upper bound on its values. Refuses, before any text exists,
// nesting deeper than depthMax (bad_header) and more than containersMax objects
// and arrays (too_large). JSON.parse builds only what this counts: it reads the
// text in the same order and stops at its first error, so even a text it
// refuses was counted up to the point it reached. A close bracket with nothing
// open is such an error, so the scan stops there and JSON.parse reports it.
function scanJsonShape(bytes, depthMax, containersMax, what) {
  let depth = 0;
  let containers = 0;
  let members = 0;
  let commas = 0;
  let inString = false;
  for (let i = 0; i < bytes.length; i += 1) {
    const c = bytes[i];
    if (inString) {
      if (c === 0x5c) i += 1; // a backslash: the escaped byte never ends the string
      else if (c === 0x22) inString = false;
      continue;
    }
    if (c === 0x22) {
      inString = true;
    } else if (c === 0x7b || c === 0x5b) {
      depth += 1;
      if (depth > depthMax) fail('bad_header', `${what} nests deeper than ${depthMax}`);
      containers += 1;
      if (containers > containersMax) fail('too_large', `${what} has more than ${containersMax} objects and arrays`);
    } else if (c === 0x7d || c === 0x5d) {
      depth -= 1;
      if (depth < 0) break;
    } else if (c === 0x3a) {
      members += 1;
    } else if (c === 0x2c) {
      commas += 1;
    }
  }
  return { containers, members, values: 1 + commas + containers };
}

// The heap a parsed graph of this shape can cost (JSON_HEAP_COSTS).
function jsonHeapBytes(byteLength, shape) {
  return JSON_HEAP_COSTS.perTextByte * byteLength + JSON_HEAP_COSTS.perValue * shape.values
    + JSON_HEAP_COSTS.perMember * shape.members + JSON_HEAP_COSTS.perContainer * shape.containers;
}

// Scan, charge ('<what> text', then `parsedWhat`), decode and parse one JSON text.
function parseJsonCharged(bytes, { depthMax, containersMax, what, textWhat, parsedWhat }, deps) {
  const shape = scanJsonShape(bytes, depthMax, containersMax, what);
  // The decoded string is at most two bytes per UTF-16 unit, and never has more units than bytes.
  reserve(deps, 2 * bytes.length, textWhat);
  reserve(deps, jsonHeapBytes(bytes.length, shape), parsedWhat);
  return parseJson(bytes, what);
}

function parseJson(bytes, what) {
  try {
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
  } catch (err) {
    return fail('bad_header', `${what} is not UTF-8 JSON`, err);
  }
}

function isCoreIdentity(value) {
  if (typeof value !== 'string' || value.length < 1 || value.length > 200) return false;
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 0x21 || code > 0x7e) return false;
  }
  return true;
}

const matches = (re, value) => typeof value === 'string' && re.test(value);

function checkIdentity(header, what) {
  if (!AUTHORING_SYSTEMS.includes(header.system)) fail('bad_header', `${what}: system`);
  if (!matches(FORMAT_RE.romHash, header.romHash)) fail('bad_header', `${what}: romHash`);
  if (!matches(FORMAT_RE.coreName, header.coreName)) fail('bad_header', `${what}: coreName`);
  if (!isCoreIdentity(header.coreIdentity)) fail('bad_header', `${what}: coreIdentity`);
}

function checkAuthorSession(header, what) {
  if (!matches(FORMAT_RE.uuid, header.authorSessionId)) fail('bad_header', `${what}: authorSessionId`);
  if (!isUint(header.segmentId, INT32_MAX)) fail('bad_header', `${what}: segmentId`);
}

function checkWindows(header, what) {
  const expected = recordableWindowsFor(header.system);
  const ok = Array.isArray(header.windows) && header.windows.length === expected.length
    && header.windows.every((w, i) => Array.isArray(w) && w.length === 2 && w[0] === expected[i][0] && w[1] === expected[i][1]);
  if (!ok) fail('bad_header', `${what}: windows`);
}

// The header's UTF-8 bytes, and the room the stored cap leaves for the body.
function encodeHeader(header, storedMax) {
  const headerBytes = textEncoder.encode(JSON.stringify(header));
  if (headerBytes.length > EVIDENCE_HEADER_BYTES_MAX) fail('too_large', 'header');
  const bodyRoom = storedMax - EVIDENCE_PREAMBLE_BYTES - headerBytes.length;
  if (bodyRoom < 1) fail('too_large', 'header leaves no room for a body');
  return { headerBytes, bodyRoom };
}

// One buffer of the file's final size, its preamble and header written; the
// caller writes the body at EVIDENCE_PREAMBLE_BYTES + headerBytes.length.
function allocateFile(magic, headerBytes, bodyLength, storedMax, deps) {
  const total = EVIDENCE_PREAMBLE_BYTES + headerBytes.length + bodyLength;
  if (total > storedMax) fail('too_large', `stored size ${total} is over ${storedMax}`);
  reserve(deps, total, 'file');
  const out = new Uint8Array(total);
  for (let i = 0; i < 4; i += 1) out[i] = magic.charCodeAt(i);
  const view = new DataView(out.buffer);
  view.setUint16(4, EVIDENCE_FORMAT_VERSION, true);
  view.setUint16(6, 0, true);
  view.setUint32(8, headerBytes.length, true);
  out.set(headerBytes, EVIDENCE_PREAMBLE_BYTES);
  return out;
}

function assemble(magic, headerBytes, body, storedMax, deps) {
  const out = allocateFile(magic, headerBytes, body.length, storedMax, deps);
  out.set(body, EVIDENCE_PREAMBLE_BYTES + headerBytes.length);
  return out;
}

// The preamble, then the header: its shape scanned and its text and parsed
// graph charged ('header text', 'parsed header') before either is made.
function readEnvelope(bytes, magic, storedMax, deps) {
  if (!(bytes instanceof Uint8Array)) throw new TypeError('evidence bytes must be a Uint8Array');
  if (bytes.length > storedMax) fail('too_large', `stored size ${bytes.length} is over ${storedMax}`);
  if (bytes.length < EVIDENCE_PREAMBLE_BYTES) fail('truncated', 'preamble');
  for (let i = 0; i < 4; i += 1) if (bytes[i] !== magic.charCodeAt(i)) fail('bad_magic');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint16(4, true) !== EVIDENCE_FORMAT_VERSION) fail('bad_version', String(view.getUint16(4, true)));
  if (view.getUint16(6, true) !== 0) fail('bad_header', 'reserved bytes');
  const headerLength = view.getUint32(8, true);
  if (headerLength < 2 || headerLength > EVIDENCE_HEADER_BYTES_MAX) fail('bad_header', 'header length');
  const bodyOffset = EVIDENCE_PREAMBLE_BYTES + headerLength;
  if (bodyOffset > bytes.length) fail('truncated', 'header');
  const header = parseJsonCharged(bytes.subarray(EVIDENCE_PREAMBLE_BYTES, bodyOffset), {
    depthMax: HEADER_JSON_DEPTH_MAX, containersMax: HEADER_JSON_CONTAINERS_MAX,
    what: 'header', textWhat: 'header text', parsedWhat: 'parsed header',
  }, deps);
  if (!isPlainObject(header)) fail('bad_header', 'header is not an object');
  return { header, headerLength, body: bytes.subarray(bodyOffset) };
}

const isOutputLimitError = (err) => err instanceof RangeError || err?.code === 'ERR_BUFFER_TOO_LARGE' || err?.code === 'too_large';

// Inflate a stored body to exactly expectedBytes; a raw body (no gzip magic)
// must already be exactly that long.
async function inflate(stored, expectedBytes, deps) {
  if (!isGzip(stored)) {
    if (stored.length < expectedBytes) fail('truncated', 'body');
    if (stored.length > expectedBytes) fail('too_large', 'body');
    return stored;
  }
  if (typeof deps?.gunzip !== 'function') throw new TypeError('deps.gunzip is required for a gzip body');
  let out;
  try {
    out = await deps.gunzip(stored, { maxOutputBytes: expectedBytes });
  } catch (err) {
    fail(isOutputLimitError(err) ? 'too_large' : 'truncated', 'body did not inflate within its declared size', err);
  }
  if (!(out instanceof Uint8Array)) throw new TypeError('deps.gunzip must return a Uint8Array');
  if (out.length < expectedBytes) fail('truncated', 'body');
  if (out.length > expectedBytes) fail('too_large', 'body');
  return out;
}

// Compress a body into at most maxOutputBytes: the injected gzip stops the
// moment its output would pass the limit (its limit error becomes too_large,
// with the cause kept), and a gzip that returns its input uncompressed (no
// CompressionStream) is held to the same limit. Any other gzip failure is the
// dependency's own error and is rethrown as it is.
async function compress(raw, maxOutputBytes, deps) {
  reserve(deps, maxOutputBytes, 'compressed body');
  let body;
  try {
    body = await deps.gzip(raw, { maxOutputBytes });
  } catch (err) {
    if (isOutputLimitError(err)) fail('too_large', `body compresses past ${maxOutputBytes} bytes`, err);
    throw err;
  }
  if (!(body instanceof Uint8Array)) throw new TypeError('deps.gzip must return a Uint8Array');
  if (body.length > maxOutputBytes) fail('too_large', `stored body ${body.length} is over ${maxOutputBytes}`);
  return body;
}

// ---- Memory recordings (spec 8.4) -------------------------------------------

// Returns the sample size in bytes.
function validateMemoryHeader(header, headerLength) {
  exactKeys(header, MEMORY_HEADER_KEYS, [], 'memory header');
  if (header.format !== EVIDENCE_FORMAT_VERSION) fail('bad_header', 'memory header: format');
  checkIdentity(header, 'memory header');
  checkAuthorSession(header, 'memory header');
  if (!FRAME_SOURCES.includes(header.frameSource)) fail('bad_header', 'memory header: frameSource');
  checkWindows(header, 'memory header');
  const caps = header.capabilities;
  if (!isPlainObject(caps)) fail('bad_header', 'memory header: capabilities');
  exactKeys(caps, ['readIO', 'readIwram'], [], 'capabilities');
  if (typeof caps.readIO !== 'boolean' || typeof caps.readIwram !== 'boolean') fail('bad_header', 'capabilities');
  if (!isTime(header.preEventMs)) fail('bad_header', 'memory header: preEventMs');
  const { samples } = header;
  if (!Array.isArray(samples) || samples.length === 0) fail('bad_header', 'memory header: samples');
  if (samples.length > FILE_CAPS.memory.samplesMax) fail('too_large', `${samples.length} samples`);
  let previous = null;
  for (const sample of samples) {
    if (!isPlainObject(sample)) fail('bad_header', 'sample');
    exactKeys(sample, ['i', 't'], ['frame'], 'sample');
    if (!isUint(sample.i, MAX_SAFE) || !isTime(sample.t)) fail('bad_header', 'sample i or t');
    if (has(sample, 'frame') && !isUint(sample.frame, MAX_SAFE)) fail('bad_header', 'sample frame');
    if (previous && (sample.i <= previous.i || sample.t <= previous.t)) fail('bad_header', 'samples must strictly increase in i and t');
    previous = sample;
  }
  if (!isUint(header.bodyBytes, MAX_SAFE)) fail('bad_header', 'memory header: bodyBytes');
  if (EVIDENCE_PREAMBLE_BYTES + headerLength + header.bodyBytes > FILE_CAPS.memory.decodedBytes) fail('too_large', 'decoded size');
  const sampleBytes = recordableSnapshotBytes(header.system);
  const minBody = 1 + sampleBytes + (samples.length - 1) * DIFF_OVERHEAD;
  const maxBody = samples.length * (1 + sampleBytes);
  if (header.bodyBytes < minBody || header.bodyBytes > maxBody) fail('bad_header', 'memory header: bodyBytes out of range');
  return sampleBytes;
}

function recordSize(record, n, sampleBytes) {
  if (!isPlainObject(record)) fail('bad_header', `sample ${n}`);
  if (record.keyframe !== undefined) {
    if (!(record.keyframe instanceof Uint8Array) || record.keyframe.length !== sampleBytes) fail('bad_header', `sample ${n} keyframe size`);
    return 1 + sampleBytes;
  }
  if (n === 0) fail('bad_header', 'the first sample must be a keyframe');
  if (!Array.isArray(record.runs)) fail('bad_header', `sample ${n} needs a keyframe or runs`);
  let size = DIFF_OVERHEAD;
  let end = 0;
  for (const run of record.runs) {
    const ok = isPlainObject(run) && run.bytes instanceof Uint8Array && run.bytes.length > 0
      && isUint(run.offset, sampleBytes - 1) && run.offset >= end && run.offset + run.bytes.length <= sampleBytes;
    if (!ok) fail('bad_header', `sample ${n} run`);
    end = run.offset + run.bytes.length;
    size += RUN_OVERHEAD + run.bytes.length;
    if (size > sampleBytes) fail('bad_header', `sample ${n} diff is not smaller than a keyframe`);
  }
  return size;
}

/**
 * Encode a memory recording (spec 8.4). Throws EvidenceFormatError too_large
 * past the sample, decoded or stored cap, each checked before the allocation
 * it bounds; deps is { gzip, reserve? } (see the top of this file).
 */
export async function encodeMemoryRecording(header, records, deps) {
  if (!isPlainObject(header)) throw new TypeError('header must be an object');
  if (!AUTHORING_SYSTEMS.includes(header.system)) fail('bad_header', 'memory header: system');
  if (!Array.isArray(records) || records.length === 0) fail('bad_header', 'at least one sample is required');
  // The count first: no record is read for a recording over the sample cap.
  if (records.length > FILE_CAPS.memory.samplesMax) fail('too_large', `${records.length} samples`);
  if (typeof deps?.gzip !== 'function') throw new TypeError('deps.gzip is required');
  const sampleBytes = recordableSnapshotBytes(header.system);
  let bodyBytes = 0;
  records.forEach((record, n) => { bodyBytes += recordSize(record, n, sampleBytes); });
  reserve(deps, HEADER_RESERVE_BYTES, 'header');
  const full = {
    format: EVIDENCE_FORMAT_VERSION,
    system: header.system,
    romHash: header.romHash,
    coreName: header.coreName,
    coreIdentity: header.coreIdentity,
    authorSessionId: header.authorSessionId,
    segmentId: header.segmentId,
    frameSource: header.frameSource,
    windows: recordableWindowsFor(header.system),
    capabilities: header.capabilities,
    preEventMs: header.preEventMs,
    bodyBytes,
    samples: records.map((r) => (r.frame === undefined ? { i: r.i, t: r.t } : { i: r.i, t: r.t, frame: r.frame })),
  };
  // Same rules as decoding, checked before the body is allocated: first every
  // field (so the header text is small), then the decoded cap with the real
  // header length.
  validateMemoryHeader(full, 0);
  const { headerBytes, bodyRoom } = encodeHeader(full, FILE_CAPS.memory.storedBytes);
  validateMemoryHeader(full, headerBytes.length);
  reserve(deps, bodyBytes, 'body');
  const raw = new Uint8Array(bodyBytes);
  const view = new DataView(raw.buffer);
  let p = 0;
  for (const record of records) {
    if (record.keyframe !== undefined) {
      raw[p] = RECORD_KEYFRAME;
      raw.set(record.keyframe, p + 1);
      p += 1 + sampleBytes;
      continue;
    }
    raw[p] = RECORD_DIFF;
    view.setUint32(p + 1, record.runs.length, true);
    p += DIFF_OVERHEAD;
    for (const run of record.runs) {
      view.setUint32(p, run.offset, true);
      view.setUint32(p + 4, run.bytes.length, true);
      raw.set(run.bytes, p + RUN_OVERHEAD);
      p += RUN_OVERHEAD + run.bytes.length;
    }
  }
  const body = await compress(raw, bodyRoom, deps);
  return assemble(EVIDENCE_MAGIC.memory, headerBytes, body, FILE_CAPS.memory.storedBytes, deps);
}

/**
 * Validate the preamble and header only (the server's spec 7.13 check). deps is
 * optional: { reserve? } charges 'header text' and 'parsed header' first.
 */
export function decodeMemoryHeader(bytes, deps = {}) {
  const { header, headerLength } = readEnvelope(bytes, EVIDENCE_MAGIC.memory, FILE_CAPS.memory.storedBytes, deps);
  validateMemoryHeader(header, headerLength);
  return header;
}

function indexMemoryRecords(raw, count, sampleBytes) {
  const offsets = new Uint32Array(count);
  const view = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
  let p = 0;
  for (let n = 0; n < count; n += 1) {
    if (p >= raw.length) fail('truncated', `sample ${n}`);
    offsets[n] = p;
    const tag = raw[p];
    if (tag === RECORD_KEYFRAME) {
      p += 1 + sampleBytes;
    } else if (tag === RECORD_DIFF && n > 0) {
      if (p + DIFF_OVERHEAD > raw.length) fail('truncated', `sample ${n}`);
      const start = p;
      const runs = view.getUint32(p + 1, true);
      p += DIFF_OVERHEAD;
      let end = 0;
      for (let r = 0; r < runs; r += 1) {
        if (p + RUN_OVERHEAD > raw.length) fail('truncated', `sample ${n} run ${r}`);
        const offset = view.getUint32(p, true);
        const length = view.getUint32(p + 4, true);
        if (length === 0 || offset < end || offset + length > sampleBytes) fail('bad_header', `sample ${n} run ${r}`);
        end = offset + length;
        p += RUN_OVERHEAD + length;
      }
      if (p - start > sampleBytes) fail('bad_header', `sample ${n} diff is not smaller than a keyframe`);
    } else {
      fail('bad_header', `sample ${n} record type ${tag}`);
    }
    if (p > raw.length) fail('truncated', `sample ${n}`);
  }
  if (p !== raw.length) fail('bad_header', 'trailing body bytes');
  return offsets;
}

/**
 * Decode the whole recording; the caller charges sampleBytes + header.bodyBytes
 * first (spec 8.8). deps is { gunzip, reserve? }: reserve charges 'header text'
 * and 'parsed header', then 'record index' before the body is inflated.
 */
export async function decodeMemoryRecording(bytes, deps) {
  const { header, headerLength, body } = readEnvelope(bytes, EVIDENCE_MAGIC.memory, FILE_CAPS.memory.storedBytes, deps);
  const sampleBytes = validateMemoryHeader(header, headerLength);
  reserve(deps, RECORD_INDEX_BASE_BYTES + 4 * header.samples.length, 'record index');
  const raw = await inflate(body, header.bodyBytes, deps);
  const recordOffsets = indexMemoryRecords(raw, header.samples.length, sampleBytes);
  return { header, sampleBytes, body: raw, recordOffsets };
}

/**
 * Rebuild every sample in order into one reused buffer and call
 * visit(sample, memory, index); copy `memory` to keep it. Returning false stops.
 */
export function replayMemorySamples(recording, visit) {
  const { header, sampleBytes, body, recordOffsets } = recording;
  const memory = new Uint8Array(sampleBytes);
  const view = new DataView(body.buffer, body.byteOffset, body.byteLength);
  for (let n = 0; n < header.samples.length; n += 1) {
    let p = recordOffsets[n];
    if (body[p] === RECORD_KEYFRAME) {
      memory.set(body.subarray(p + 1, p + 1 + sampleBytes));
    } else {
      const runs = view.getUint32(p + 1, true);
      p += DIFF_OVERHEAD;
      for (let r = 0; r < runs; r += 1) {
        const offset = view.getUint32(p, true);
        const length = view.getUint32(p + 4, true);
        memory.set(body.subarray(p + RUN_OVERHEAD, p + RUN_OVERHEAD + length), offset);
        p += RUN_OVERHEAD + length;
      }
    }
    if (visit(header.samples[n], memory, n) === false) return;
  }
}

/** Changed-byte runs from one snapshot to the next; gaps shorter than a run header are merged. */
export function diffRuns(previous, current) {
  if (!(previous instanceof Uint8Array) || !(current instanceof Uint8Array) || previous.length !== current.length) {
    throw new TypeError('diffRuns needs two Uint8Arrays of the same length');
  }
  const spans = [];
  let start = -1;
  let last = -1;
  for (let i = 0; i < current.length; i += 1) {
    if (previous[i] === current[i]) continue;
    if (start >= 0 && i - last - 1 >= RUN_OVERHEAD) {
      spans.push([start, last]);
      start = -1;
    }
    if (start < 0) start = i;
    last = i;
  }
  if (start >= 0) spans.push([start, last]);
  return spans.map(([from, to]) => ({ offset: from, bytes: current.slice(from, to + 1) }));
}

// ---- Frame sequences (spec 8.5) ---------------------------------------------

/** Width and height from a JPEG's SOF marker, or null when there is none. */
export function jpegDimensions(bytes) {
  if (!(bytes instanceof Uint8Array) || bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let p = 2;
  while (p + 1 < bytes.length) {
    if (bytes[p] !== 0xff) return null;
    const marker = bytes[p + 1];
    if (marker === 0xff) {
      p += 1; // fill byte
      continue;
    }
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
      p += 2; // markers without a length
      continue;
    }
    if (marker === 0xd9 || marker === 0xda) return null; // end of image or scan before any frame header
    if (p + 4 > bytes.length) return null;
    const length = (bytes[p + 2] << 8) | bytes[p + 3];
    if (length < 2 || p + 2 + length > bytes.length) return null;
    const frameHeader = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (frameHeader) {
      if (length < 7) return null;
      const height = (bytes[p + 5] << 8) | bytes[p + 6];
      const width = (bytes[p + 7] << 8) | bytes[p + 8];
      return width > 0 && height > 0 ? { width, height } : null;
    }
    p += 2 + length;
  }
  return null;
}

function validateFramesHeader(header) {
  const { kind } = header;
  if (kind !== 'frames' && kind !== 'test_frames') fail('bad_header', 'frames header: kind');
  exactKeys(header, FRAMES_HEADER_KEYS[kind], [], 'frames header');
  if (header.format !== EVIDENCE_FORMAT_VERSION) fail('bad_header', 'frames header: format');
  checkIdentity(header, 'frames header');
  if (kind === 'frames') checkAuthorSession(header, 'frames header');
  else if (!matches(FORMAT_RE.uuid, header.testSessionId)) fail('bad_header', 'frames header: testSessionId');
  const caps = FILE_CAPS[kind];
  const list = header.frames;
  if (!Array.isArray(list) || list.length === 0) fail('bad_header', 'frames header: frames');
  if (list.length > caps.framesMax) fail('too_large', `${list.length} frames`);
  let previous = null;
  for (const frame of list) {
    if (!isPlainObject(frame)) fail('bad_header', 'frame');
    exactKeys(frame, FRAME_ENTRY_KEYS[kind], [], 'frame');
    if (kind === 'frames' && !isUint(frame.sampleIndex, MAX_SAFE)) fail('bad_header', 'frame sampleIndex');
    if (!isTime(frame.t)) fail('bad_header', 'frame t');
    if (!isUint(frame.width, 0xffff) || !isUint(frame.height, 0xffff) || frame.width === 0 || frame.height === 0) {
      fail('bad_header', 'frame size');
    }
    if (frame.width > caps.maxWidth || frame.height > caps.maxHeight) fail('too_large', `frame ${frame.width}x${frame.height}`);
    if (previous && (frame.t <= previous.t || (kind === 'frames' && frame.sampleIndex <= previous.sampleIndex))) {
      fail('bad_header', 'frames must strictly increase');
    }
    previous = frame;
  }
}

function indexFrames(header, body) {
  const view = new DataView(body.buffer, body.byteOffset, body.byteLength);
  const images = [];
  let p = 0;
  header.frames.forEach((frame, n) => {
    if (p + 4 > body.length) fail('truncated', `frame ${n}`);
    const length = view.getUint32(p, true);
    p += 4;
    if (length < 4) fail('bad_header', `frame ${n} length`);
    if (p + length > body.length) fail('truncated', `frame ${n}`);
    const jpeg = body.subarray(p, p + length);
    p += length;
    const size = jpegDimensions(jpeg);
    if (!size) fail('bad_header', `frame ${n} is not a JPEG`);
    if (size.width !== frame.width || size.height !== frame.height) fail('bad_header', `frame ${n} size does not match its header`);
    images.push(jpeg);
  });
  if (p !== body.length) fail('bad_header', 'trailing body bytes');
  return images;
}

/**
 * Encode a frame sequence (spec 8.5); dimensions come from each JPEG's own
 * header. The frame count and the file's total size are checked before any
 * JPEG is parsed, and the file is written into one buffer of its final size.
 * deps is optional: { reserve? } (see the top of this file).
 */
export function encodeFrames(header, frames, deps = {}) {
  if (!isPlainObject(header)) throw new TypeError('header must be an object');
  if (header.kind !== 'frames' && header.kind !== 'test_frames') fail('bad_header', 'frames header: kind');
  if (!Array.isArray(frames) || frames.length === 0) fail('bad_header', 'at least one frame is required');
  const caps = FILE_CAPS[header.kind];
  if (frames.length > caps.framesMax) fail('too_large', `${frames.length} frames`);
  let bodyLength = 0;
  frames.forEach((frame, n) => {
    if (!isPlainObject(frame) || !(frame.jpeg instanceof Uint8Array)) fail('bad_header', `frame ${n}`);
    bodyLength += 4 + frame.jpeg.length;
    if (EVIDENCE_PREAMBLE_BYTES + bodyLength > caps.storedBytes) fail('too_large', `frames body over ${caps.storedBytes}`);
  });
  reserve(deps, HEADER_RESERVE_BYTES, 'header');
  const entries = frames.map((frame, n) => {
    const size = jpegDimensions(frame.jpeg);
    if (!size) fail('bad_header', `frame ${n} is not a JPEG`);
    return header.kind === 'frames'
      ? { sampleIndex: frame.sampleIndex, t: frame.t, width: size.width, height: size.height }
      : { t: frame.t, width: size.width, height: size.height };
  });
  const identity = { system: header.system, romHash: header.romHash, coreName: header.coreName, coreIdentity: header.coreIdentity };
  const full = header.kind === 'frames'
    ? { format: EVIDENCE_FORMAT_VERSION, kind: 'frames', ...identity, authorSessionId: header.authorSessionId, segmentId: header.segmentId, frames: entries }
    : { format: EVIDENCE_FORMAT_VERSION, kind: 'test_frames', ...identity, testSessionId: header.testSessionId, frames: entries };
  validateFramesHeader(full);
  const { headerBytes } = encodeHeader(full, caps.storedBytes);
  const out = allocateFile(EVIDENCE_MAGIC.frames, headerBytes, bodyLength, caps.storedBytes, deps);
  const view = new DataView(out.buffer);
  let p = EVIDENCE_PREAMBLE_BYTES + headerBytes.length;
  for (const frame of frames) {
    view.setUint32(p, frame.jpeg.length, true);
    out.set(frame.jpeg, p + 4);
    p += 4 + frame.jpeg.length;
  }
  return out;
}

function readFrames(bytes, deps) {
  const { header, body } = readEnvelope(bytes, EVIDENCE_MAGIC.frames, FRAMES_STORED_MAX, deps);
  validateFramesHeader(header);
  if (bytes.length > FILE_CAPS[header.kind].storedBytes) fail('too_large', `stored size ${bytes.length}`);
  reserve(deps, FRAME_INDEX_BYTES_PER_FRAME * header.frames.length, 'frame index');
  return { header, images: indexFrames(header, body) };
}

/**
 * Validate the header, every length prefix and every JPEG's dimensions; return
 * the header. deps is optional: { reserve? } charges 'header text', 'parsed
 * header' and 'frame index', each before it is made.
 */
export function decodeFramesHeader(bytes, deps = {}) {
  return readFrames(bytes, deps).header;
}

/** The header and each JPEG (views into `bytes`); deps as decodeFramesHeader's. */
export function decodeFrames(bytes, deps = {}) {
  return readFrames(bytes, deps);
}

// ---- Checkpoint envelopes (spec 8.6) -----------------------------------------

function validateCheckpointHeader(header, headerLength) {
  exactKeys(header, CHECKPOINT_HEADER_KEYS, [], 'checkpoint header');
  if (header.format !== EVIDENCE_FORMAT_VERSION) fail('bad_header', 'checkpoint header: format');
  checkIdentity(header, 'checkpoint header');
  checkAuthorSession(header, 'checkpoint header');
  if (!isTime(header.t)) fail('bad_header', 'checkpoint header: t');
  checkWindows(header, 'checkpoint header');
  if (!isUint(header.saveBytes, MAX_SAFE) || header.saveBytes < 2) fail('bad_header', 'checkpoint header: saveBytes');
  if (!isUint(header.bodyBytes, MAX_SAFE)) fail('bad_header', 'checkpoint header: bodyBytes');
  if (EVIDENCE_PREAMBLE_BYTES + headerLength + header.bodyBytes > CHECKPOINT_CAPS.decodedBytes) fail('too_large', 'decoded size');
  if (header.memoryT === null) {
    if (header.memoryBytes !== 0) fail('bad_header', 'checkpoint header: memoryBytes without memoryT');
  } else {
    if (!isTime(header.memoryT) || header.memoryT < header.t) fail('bad_header', 'checkpoint header: memoryT');
    if (header.memoryBytes !== recordableSnapshotBytes(header.system)) fail('bad_header', 'checkpoint header: memoryBytes');
  }
  if (header.bodyBytes !== 1 + header.saveBytes + header.memoryBytes) fail('bad_header', 'checkpoint header: bodyBytes');
}

// Canonical base64 (the alphabet atob accepts, length a multiple of 4, at most
// two trailing '=' pads). A character loop rather than a regular expression, so
// a 16 MiB state never stresses a regex engine; a trailing newline fails it.
function isBase64(text) {
  if (typeof text !== 'string' || text.length === 0 || text.length % 4 !== 0) return false;
  let pad = 0;
  for (let i = 0; i < text.length; i += 1) {
    const c = text.charCodeAt(i);
    if (c === 0x3d) {
      if (i < text.length - 2) return false;
      pad += 1;
      continue;
    }
    if (pad > 0) return false;
    const ok = (c >= 0x41 && c <= 0x5a) || (c >= 0x61 && c <= 0x7a) || (c >= 0x30 && c <= 0x39) || c === 0x2b || c === 0x2f;
    if (!ok) return false;
  }
  return true;
}

// The state's representation follows the core (spec 8.6): jsnes keeps a plain
// object; every libretro core's state is non-empty bytes before encoding and
// canonical base64 after (`encoded` is true for a save read back from JSON).
function validateSave(save, header, encoded) {
  if (!isPlainObject(save)) fail('bad_header', 'save must be an object');
  exactKeys(save, SAVE_KEYS, [BUNDLE_KEY], 'save');
  if (save.system !== header.system || save.romHash !== header.romHash || save.coreName !== header.coreName) {
    fail('bad_header', 'save identity does not match the header');
  }
  if (!matches(SAVE_MODE_RE, save.mode)) fail('bad_header', 'save mode');
  if (typeof save.isMultiDisk !== 'boolean') fail('bad_header', 'save isMultiDisk');
  if (save.isMultiDisk !== has(save, BUNDLE_KEY)) fail('bad_header', `${BUNDLE_KEY} is carried exactly by multi-disk saves`);
  if (has(save, BUNDLE_KEY) && !isBase64(save[BUNDLE_KEY])) fail('bad_header', `save ${BUNDLE_KEY} is not base64`);
  if (!isUint(save.frame, MAX_SAFE)) fail('bad_header', 'save frame');
  const state = save.emulatorState;
  if (save.coreName === JSNES_CORE) {
    if (!isPlainObject(state)) fail('bad_header', 'save emulatorState: a jsnes state is a plain object');
  } else if (encoded ? !isBase64(state) : !(state instanceof Uint8Array && state.length > 0)) {
    fail('bad_header', `save emulatorState: a ${save.coreName} state is ${encoded ? 'base64 text' : 'non-empty bytes'}`);
  }
}

// ---- Exact sizes of the save text, computed before anything is built --------

const base64Length = (byteLength) => 4 * Math.ceil(byteLength / 3);

// UTF-8 length of JSON.stringify(text): quotes, the two-character escapes, the
// \u00XX escapes for other control characters and lone surrogates, then 1 to
// 4 bytes per character. Stops counting once past `limit`.
function jsonStringBytes(text, limit = Infinity) {
  if (text.length + 2 > limit) return text.length + 2;
  let n = 2;
  for (let i = 0; i < text.length && n <= limit; i += 1) {
    const c = text.charCodeAt(i);
    if (c === 0x22 || c === 0x5c || c === 0x08 || c === 0x09 || c === 0x0a || c === 0x0c || c === 0x0d) n += 2;
    else if (c < 0x20) n += 6;
    else if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff) {
      const next = i + 1 < text.length ? text.charCodeAt(i + 1) : 0;
      if (next >= 0xdc00 && next <= 0xdfff) {
        n += 4;
        i += 1;
      } else {
        n += 6;
      }
    } else if (c >= 0xdc00 && c <= 0xdfff) n += 6;
    else n += 3;
  }
  return n;
}

// Length of JSON.stringify(value) for a number: null when not finite.
function jsonNumberBytes(value) {
  if (!Number.isFinite(value)) return 4;
  if (Number.isSafeInteger(value)) {
    let digits = value < 0 ? 2 : 1;
    for (let v = Math.abs(value); v >= 10; v = Math.floor(v / 10)) digits += 1;
    return digits;
  }
  return String(value).length;
}

// UTF-8 length of JSON.stringify(value) for plain JSON data (plain objects and
// arrays of strings, numbers, booleans and null, with undefined members, which
// is what jsnes toJSON returns), computed without building the text and
// returning early, above `limit`, once past it. Anything else (typed arrays,
// class instances, functions, bigints), nesting deeper than 64 (bad_header) or
// more than 65,536 objects and arrays (too_large) is refused: the decoder's
// rules, so every state the encoder writes decodes.
function jsonDataBytes(value, limit, depth = 0, counter = { containers: 0 }) {
  if (value === null) return 4;
  switch (typeof value) {
    case 'boolean': return value ? 4 : 5;
    case 'number': return jsonNumberBytes(value);
    case 'string': return jsonStringBytes(value, limit);
    case 'object': break;
    default: return fail('bad_header', 'save emulatorState is not plain JSON data');
  }
  if (depth >= JSON_DEPTH_MAX) fail('bad_header', 'save emulatorState nests too deeply');
  counter.containers += 1;
  if (counter.containers > JSNES_JSON_CONTAINERS_MAX) {
    fail('too_large', `save emulatorState has more than ${JSNES_JSON_CONTAINERS_MAX} objects and arrays`);
  }
  if (Array.isArray(value)) {
    let n = 2 + Math.max(0, value.length - 1);
    for (let i = 0; i < value.length && n <= limit; i += 1) {
      n += value[i] === undefined ? 4 : jsonDataBytes(value[i], limit - n, depth + 1, counter);
    }
    return n;
  }
  if (!isPlainObject(value)) fail('bad_header', 'save emulatorState is not plain JSON data');
  let n = 2;
  let members = 0;
  for (const key in value) {
    if (!has(value, key) || value[key] === undefined) continue;
    n += (members > 0 ? 1 : 0) + jsonStringBytes(key) + 1 + jsonDataBytes(value[key], limit - n, depth + 1, counter);
    members += 1;
    if (n > limit) return n;
  }
  return n;
}

function writeAscii(out, p, text) {
  for (let i = 0; i < text.length; i += 1) out[p + i] = text.charCodeAt(i);
  return p + text.length;
}

// The base64 text of `bytes`, written into `out` as ASCII a chunk at a time,
// so no full-size binary or base64 string is ever built. Returns the end.
function writeBase64(out, p, bytes) {
  let q = p;
  for (let i = 0; i < bytes.length; i += BASE64_CHUNK) {
    q = writeAscii(out, q, btoa(String.fromCharCode.apply(null, bytes.subarray(i, i + BASE64_CHUNK))));
  }
  return q;
}

/**
 * Encode a checkpoint: provenance header, save object and optional memory copy
 * (spec 8.6). The save JSON is the object { system, romHash, coreName, mode,
 * isMultiDisk, frame, emulatorState, romDataB64? } with emulatorState as
 * encodeEmulatorState writes it (base64 for libretro bytes, the object for
 * jsnes) and romDataB64 the C64 multi-disk bundle's base64 (app._romDataB64).
 * Its exact size is computed first, so the decoded cap is checked before any
 * text or body exists, and the JSON is written straight into the body. deps is
 * { gzip, reserve? } (see the top of this file).
 */
export async function encodeCheckpointEnvelope(header, save, memory, deps) {
  if (!isPlainObject(header)) throw new TypeError('header must be an object');
  if (!AUTHORING_SYSTEMS.includes(header.system)) fail('bad_header', 'checkpoint header: system');
  if (typeof deps?.gzip !== 'function') throw new TypeError('deps.gzip is required');
  checkIdentity(header, 'checkpoint header');
  checkAuthorSession(header, 'checkpoint header');
  const sampleBytes = recordableSnapshotBytes(header.system);
  if (memory !== null && !(memory instanceof Uint8Array && memory.length === sampleBytes)) {
    fail('bad_header', 'memory must be null or one recordable-window sample');
  }
  validateSave(save, header, false);
  reserve(deps, HEADER_RESERVE_BYTES, 'header');
  const jsnes = save.coreName === JSNES_CORE;
  // Every field before emulatorState is validated ASCII, so its JSON is small and
  // its length is its UTF-8 size; the bundle is validated base64, which JSON never escapes.
  const head = `${JSON.stringify({
    system: save.system, romHash: save.romHash, coreName: save.coreName,
    mode: save.mode, isMultiDisk: save.isMultiDisk, frame: save.frame,
  }).slice(0, -1)},"emulatorState":`;
  const bundleHead = `,"${BUNDLE_KEY}":"`;
  const stateBytes = jsnes
    ? jsonDataBytes(save.emulatorState, CHECKPOINT_CAPS.decodedBytes)
    : 2 + base64Length(save.emulatorState.length);
  const saveBytes = head.length + stateBytes + (save.isMultiDisk ? bundleHead.length + save[BUNDLE_KEY].length + 1 : 0) + 1;
  const memoryBytes = memory ? memory.length : 0;
  const full = {
    format: EVIDENCE_FORMAT_VERSION,
    system: header.system,
    romHash: header.romHash,
    coreName: header.coreName,
    coreIdentity: header.coreIdentity,
    authorSessionId: header.authorSessionId,
    segmentId: header.segmentId,
    t: header.t,
    memoryT: header.memoryT,
    windows: recordableWindowsFor(header.system),
    saveBytes,
    memoryBytes,
    bodyBytes: 1 + saveBytes + memoryBytes,
  };
  // Same rules as decoding, before any state text or body exists: first every
  // field, then the decoded cap with the real header length.
  validateCheckpointHeader(full, 0);
  const { headerBytes, bodyRoom } = encodeHeader(full, CHECKPOINT_CAPS.storedBytes);
  validateCheckpointHeader(full, headerBytes.length);
  let stateText = null;
  if (jsnes) {
    // The one text the encoder builds: at most 2 bytes per UTF-16 unit, and
    // never more units than the UTF-8 bytes computed above.
    reserve(deps, 2 * stateBytes, 'state JSON');
    stateText = JSON.stringify(save.emulatorState);
  }
  reserve(deps, full.bodyBytes, 'body');
  const raw = new Uint8Array(full.bodyBytes);
  raw[0] = RECORD_CHECKPOINT;
  let p = writeAscii(raw, 1, head);
  if (jsnes) {
    const { read, written } = textEncoder.encodeInto(stateText, raw.subarray(p, p + stateBytes));
    if (read !== stateText.length || written !== stateBytes) fail('bad_header', 'save emulatorState changed while it was encoded');
    p += written;
  } else {
    raw[p] = 0x22;
    p = writeBase64(raw, p + 1, save.emulatorState);
    raw[p] = 0x22;
    p += 1;
  }
  if (save.isMultiDisk) {
    p = writeAscii(raw, p, bundleHead);
    p = writeAscii(raw, p, save[BUNDLE_KEY]);
    raw[p] = 0x22;
    p += 1;
  }
  raw[p] = 0x7d;
  p += 1;
  if (memory) raw.set(memory, p);
  const body = await compress(raw, bodyRoom, deps);
  return assemble(EVIDENCE_MAGIC.checkpoint, headerBytes, body, CHECKPOINT_CAPS.storedBytes, deps);
}

/**
 * Validate the preamble and provenance header only (spec 7.13). deps is
 * optional: { reserve? } charges 'header text' and 'parsed header' first.
 */
export function decodeCheckpointHeader(bytes, deps = {}) {
  const { header, headerLength } = readEnvelope(bytes, EVIDENCE_MAGIC.checkpoint, CHECKPOINT_CAPS.storedBytes, deps);
  validateCheckpointHeader(header, headerLength);
  return header;
}

// Bytes a canonical base64 text decodes to (isBase64 has already held).
function base64DecodedLength(text) {
  const pads = text.endsWith('==') ? 2 : text.endsWith('=') ? 1 : 0;
  return (text.length / 4) * 3 - pads;
}

// Decode canonical base64 into `out`, which is exactly its decoded length, one
// chunk at a time, so the full-size binary string atob would build never
// exists. The bytes are those base64ToU8 produces.
function decodeBase64Into(text, out) {
  let q = 0;
  for (let i = 0; i < text.length; i += BASE64_DECODE_CHUNK) {
    const binary = atob(text.slice(i, i + BASE64_DECODE_CHUNK));
    for (let j = 0; j < binary.length; j += 1) out[q + j] = binary.charCodeAt(j);
    q += binary.length;
  }
  if (q !== out.length) throw new RangeError(`base64 decoded to ${q} bytes, not ${out.length}`);
}

/**
 * Decode the save and the memory copy: a libretro state back to bytes (a new
 * Uint8Array); a jsnes state stays the plain object it was (never turned into
 * bytes, even when its keys look like a stringified byte array). `memory` is a
 * view into the inflated body, which is `bytes` itself when the body is stored
 * raw; copy it to keep it past `bytes`.
 *
 * Retained bytes (spec 6.5, 8.8): the caller charges header.bodyBytes (from
 * decodeCheckpointHeader) before calling, as before. deps.reserve(bytes, what),
 * optional, is called before each allocation, in this order, and throws to
 * refuse: the decoder then allocates nothing more and the caller's own error
 * reaches the caller unchanged, as an encoder's does. Without it nothing is
 * charged and the result is the same.
 *   'header text', 'parsed header'  as every decoder (see the top of this file).
 *   'save text'    2 x saveBytes: the UTF-16 string the save JSON is read into.
 *                  Charged with 'save object' before either is made, once the
 *                  save's shape is scanned: a libretro save must be one flat
 *                  object, a jsnes state at most 64 deep with at most 65,536
 *                  objects and arrays, or nothing is read.
 *   'save object'  the parsed save, from that shape (JSON_HEAP_COSTS): for a
 *                  libretro save the base64 text again and a few members, for
 *                  a jsnes save its whole object graph.
 *   'state bytes'  libretro only: the decoded state, charged once the save is
 *                  parsed and checked, before any of the base64 is decoded.
 * The base64 is decoded straight into that buffer one 32,768-character chunk at
 * a time, so no full-size binary string exists; the one chunk of scratch is not
 * charged.
 */
export async function decodeCheckpointEnvelope(bytes, deps) {
  const { header, headerLength, body } = readEnvelope(bytes, EVIDENCE_MAGIC.checkpoint, CHECKPOINT_CAPS.storedBytes, deps);
  validateCheckpointHeader(header, headerLength);
  const raw = await inflate(body, header.bodyBytes, deps);
  if (raw[0] !== RECORD_CHECKPOINT) fail('bad_header', 'checkpoint record type');
  // validateSave holds the save's core to the header's, so the header decides.
  const jsnes = header.coreName === JSNES_CORE;
  // The save object itself is one level more than a jsnes state's own nesting.
  const save = parseJsonCharged(raw.subarray(1, 1 + header.saveBytes), {
    depthMax: jsnes ? 1 + JSON_DEPTH_MAX : 1,
    containersMax: jsnes ? 1 + JSNES_JSON_CONTAINERS_MAX : 1,
    what: 'save', textWhat: 'save text', parsedWhat: 'save object',
  }, deps);
  validateSave(save, header, true);
  const memory = header.memoryBytes ? raw.subarray(1 + header.saveBytes) : null;
  if (jsnes) return { header, save, memory };
  const text = save.emulatorState;
  const stateLength = base64DecodedLength(text);
  reserve(deps, stateLength, 'state bytes');
  const state = new Uint8Array(stateLength);
  try {
    decodeBase64Into(text, state);
  } catch (err) {
    // atob refuses what isBase64 accepted only if the two ever disagree; stay typed.
    return fail('bad_header', 'save emulatorState did not decode', err);
  }
  save.emulatorState = state;
  return { header, save, memory };
}

/**
 * The save object the existing restore path reads, from a decoded checkpoint's
 * save (spec 8.6): the same fields under a manual save's names
 * (src/client/modules/manual-save.js), so a C64 bundle's base64 becomes romData,
 * which main.js _resolveSaveRom reads back with JSON.parse(atob(save.romData)).
 * emulatorState is passed through as decoded (bytes, or a jsnes object), the
 * representation applySaveInPlace takes.
 */
export function checkpointRestoreSave(save) {
  if (!isPlainObject(save)) throw new TypeError('checkpointRestoreSave needs a decoded checkpoint save');
  const { [BUNDLE_KEY]: bundle, ...rest } = save;
  return bundle === undefined ? rest : { ...rest, romData: bundle };
}

// ---- Spec 7.13 rules shared by the client and the server ----------------------

/** The recording starts at or before the event and runs 250 ms past it. */
export function memoryCoversEvent(memoryHeader, eventTMs) {
  const { samples } = memoryHeader;
  return isTime(eventTMs) && samples.length > 0 && samples[0].t <= eventTMs
    && samples[samples.length - 1].t >= eventTMs + TIMINGS.postEventMinMs;
}

/** Every frame's (sampleIndex, t) equals a sample entry of the attached recording. */
export function framesMatchMemory(framesHeader, memoryHeader) {
  if (framesHeader.kind !== 'frames') return false;
  const byIndex = new Map(memoryHeader.samples.map((sample) => [sample.i, sample.t]));
  return framesHeader.frames.every((frame) => byIndex.get(frame.sampleIndex) === frame.t);
}

/** A test run's frames span its event time. */
export function testFramesCoverEvent(framesHeader, eventTMs) {
  const { frames } = framesHeader;
  return framesHeader.kind === 'test_frames' && isTime(eventTMs) && frames.length > 0
    && frames[0].t <= eventTMs && frames[frames.length - 1].t >= eventTMs;
}

/** checkpoint_near: at least 2,000 ms before the event; checkpoint_early: before it (spec 7.13, 8.6). */
export function checkpointPrecedesEvent(fileKind, checkpointT, eventTMs) {
  if (fileKind !== 'checkpoint_near' && fileKind !== 'checkpoint_early') {
    throw new RangeError(`not a checkpoint file kind: ${String(fileKind)}`);
  }
  if (!isTime(checkpointT) || !isTime(eventTMs)) return false;
  return fileKind === 'checkpoint_near'
    ? eventTMs - checkpointT >= TIMINGS.checkpointNearGapMs
    : checkpointT < eventTMs;
}
